import { useEffect, useState } from "react";
import {
  BoxedTable,
  Button,
  DashCard,
  Input,
  PageContainer,
  Select,
} from "../../../components";
import { alunoApi, professoresApi } from "../../../services";
import {
  AlunoCurso,
  Avulso,
  Intensivo,
  Professor,
  Repasse,
} from "../../../models";
import Alert from "../../../helpers/Alert";
import dayjs from "dayjs";
import Str from "../../../helpers/Str";
import { BiMoneyWithdraw } from "react-icons/bi";
import { IHorariosSalasTurnos } from "../../../types";
import { Oficina } from "../../../models/Oficina";

type CommonProps = {
  dia_semana?: string;
  horario_formatted?: string;
  "100_formatted"?: string;
  60?: number;
  "60_formatted"?: string;
  40?: number;
  "40_formatted"?: string;
  pago_formatted?: string;
  soma_materiais_formatted?: string;
  soma_materiais: number;
};

type Item = AlunoCurso &
  CommonProps & {
    aluno_nome?: string;
    inicio_formatted?: string;
    fim_formatted?: string;
    pagamento_formatted?: string;
    planos_formatted?: string;
    100?: number;
    porcentagem_desconto_formatted?: string;
    codigo_sala?: string;
  };

type AvulsoItem = Avulso &
  CommonProps & {
    valor_avulso: number;
    data_formatted?: string;
    inicio_aula?: string;
    final_aula?: string;
    materiais?: Repasse[];
  };

type IntensivoItem = Intensivo &
  IHorariosSalasTurnos &
  CommonProps & {
    data: string;
    valor_avulso: number;
    data_formatted?: string;
    inicio_aula?: string;
    final_aula?: string;
  };

type OficinaItem = Oficina &
  IHorariosSalasTurnos &
  CommonProps & {
    data: string;
    valor_avulso: number;
    data_formatted?: string;
    inicio_aula?: string;
    final_aula?: string;
  };

type Response = {
  contratos?: Item[];
  avulsos?: AvulsoItem[];
  intensivos?: IntensivoItem[];
  oficinas?: OficinaItem[];
};

export default function ProfessoresContratosResumo() {
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState<{ start: string; end: string }[]>([]);
  const [professores, setProfessores] = useState<Professor[]>([]);
  const [filter, setFilter] = useState({
    professor_id: undefined,
    year: dayjs().year(),
    month: dayjs().month() + 1,
    week: "semana-0",
  });

  const professor = professores.find((item) => item.id === filter.professor_id);

  const filterSemanaIndex = Number(filter.week.replace("semana-", ""));

  const [currentFilter, setCurrentFilter] = useState<typeof filter>({} as any);

  const [response, setResponse] = useState<Response>({});

  const contratos = (response?.contratos ?? []).map((item) => {
    // Caso o data seja uma string, converte para objeto
    const data =
      typeof item.data === "string" ? JSON.parse(item.data) : item.data;

    // Horário
    item.horario_formatted =
      data.dias_semana.split(" - ")[1].replace(" até ", " - ") + "H";

    // Inicio / Fim
    item.inicio_formatted = data.inicio_plano;
    item.fim_formatted = data.fim_plano;

    // Pagamento
    item.pagamento_formatted =
      dayjs(data.meses_pagamento[0]).month() +
      1 +
      "|" +
      (dayjs(data.meses_pagamento[data.meses_pagamento.length - 1]).month() +
        1);

    // Pago
    item.pago_formatted = item.pagamentos?.find(
      (pagamento) =>
        Number(pagamento.ano) === Number(currentFilter.year) &&
        Number(pagamento.mes) === Number(currentFilter.month)
    )
      ? "PG"
      : "";

    // Plano
    item.planos_formatted = (
      Array.isArray(data.planos) ? data.planos.join(",") : data.planos
    )
      .toUpperCase()
      .replaceAll("MENSAL", "MEN")
      .replaceAll("TRIMESTRAL", "TRI")
      .replaceAll("SEMESTRAL", "SEM");

    // 60/40
    item["100"] = data.valor_mes;
    item["60"] = data.valor_mes * 0.6;
    item["40"] = data.valor_mes * 0.4;
    item["100_formatted"] = Str.currencyMask(item["100"]);
    item["60_formatted"] = Str.currencyMask(item["60"]);
    item["40_formatted"] = Str.currencyMask(item["40"]);
    item.soma_materiais_formatted = Str.currencyMask(item.soma_materiais);

    // Desconto
    item.porcentagem_desconto_formatted = Str.formatNumber(
      item.porcentagem_desconto
    );
    item.porcentagem_desconto_formatted =
      Number(item.porcentagem_desconto_formatted) > 0
        ? item.porcentagem_desconto_formatted
        : "";

    // Dia semana
    item.dia_semana = dayjs(item.data_inicio)
      .locale("pt-br")
      .format("ddd")
      .toUpperCase();

    return item;
  });

  const avulsos = (response?.avulsos ?? []).map((item) => {
    item.horario_formatted = `${item.inicio_aula?.slice(
      0,
      5
    )}-${item.final_aula?.slice(0, 5)}H`;
    item.dia_semana = dayjs(item.data)
      .locale("pt-br")
      .format("ddd")
      .toUpperCase();
    item.pago_formatted = "PG";
    item.nome = item.nome.toUpperCase();
    item.data_formatted = dayjs(item.data).format("DD/MM/YYYY");
    item["60"] = item.valor_avulso * 0.6;
    item["40"] = item.valor_avulso * 0.4;
    item["60_formatted"] = Str.currencyMask(item["60"]);
    item["40_formatted"] = Str.currencyMask(item["40"]);
    item["100_formatted"] = Str.currencyMask(item.valor_avulso);
    item.soma_materiais_formatted = Str.currencyMask(item.soma_materiais);
    return item;
  });

  const intensivos = (response?.intensivos ?? []).map((item) => {
    item.horario_formatted = `${item.inicio_aula?.slice(
      0,
      5
    )}-${item.final_aula?.slice(0, 5)}H`;
    item.dia_semana = dayjs(item.data)
      .locale("pt-br")
      .format("ddd")
      .toUpperCase();
    item.pago_formatted = "PG";
    item.nome = item.nome.toUpperCase();
    item.data_formatted = dayjs(item.data).format("DD/MM/YYYY");
    item["60"] = item.valor_avulso * 0.6;
    item["40"] = item.valor_avulso * 0.4;
    item["60_formatted"] = Str.currencyMask(item["60"]);
    item["40_formatted"] = Str.currencyMask(item["40"]);
    item["100_formatted"] = Str.currencyMask(item.valor_avulso);
    item.soma_materiais_formatted = Str.currencyMask(item.soma_materiais);
    return item;
  });

  const oficinas = (response?.oficinas ?? []).map((item) => {
    item.horario_formatted = `${item.inicio_aula?.slice(
      0,
      5
    )}-${item.final_aula?.slice(0, 5)}H`;
    item.dia_semana = dayjs(item.data)
      .locale("pt-br")
      .format("ddd")
      .toUpperCase();
    item.pago_formatted = "PG";
    item.nome = item.nome.toUpperCase();
    item.data_formatted = dayjs(item.data).format("DD/MM/YYYY");
    item["60"] = item.valor_avulso * 0.6;
    item["40"] = item.valor_avulso * 0.4;
    item["60_formatted"] = Str.currencyMask(item["60"]);
    item["40_formatted"] = Str.currencyMask(item["40"]);
    item["100_formatted"] = Str.currencyMask(item.valor_avulso);
    item.soma_materiais_formatted = Str.currencyMask(item.soma_materiais);

    return item;
  });

  const sumMateriaisContratos = contratos.reduce(
    (acc: number, item) => acc + Number(item.soma_materiais ?? 0),
    0
  );
  const sum40Contratos = contratos.reduce(
    (acc: number, item) => acc + Number(item["40"]),
    0
  );
  const sum60Contratos = contratos.reduce(
    (acc: number, item) => acc + Number(item["60"]),
    0
  );
  const sum100Contratos = contratos.reduce(
    (acc: number, item) => acc + Number(item["100"]),
    0
  );

  const sumMateriaisAvulsos = avulsos.reduce(
    (acc: number, item) => acc + Number(item.soma_materiais ?? 0),
    0
  );
  const sum40Avulsos = avulsos.reduce(
    (acc: number, item) => acc + Number(item["40"]),
    0
  );
  const sum60Avulsos = avulsos.reduce(
    (acc: number, item) => acc + Number(item["60"]),
    0
  );
  const sum100Avulsos = avulsos.reduce(
    (acc: number, item) => acc + Number(item.valor_avulso),
    0
  );

  const sumMateriaisIntensivos = intensivos.reduce(
    (acc: number, item) => acc + Number(item.soma_materiais ?? 0),
    0
  );
  const sum40Intensivos = intensivos.reduce(
    (acc: number, item) => acc + Number(item["40"]),
    0
  );
  const sum60Intensivos = intensivos.reduce(
    (acc: number, item) => acc + Number(item["60"]),
    0
  );
  const sum100Intensivos = intensivos.reduce(
    (acc: number, item) => acc + Number(item.valor_avulso),
    0
  );
  const sumMateriaisOficinas = oficinas.reduce(
    (acc: number, item) => acc + Number(item.soma_materiais ?? 0),
    0
  );
  const sum40Oficinas = oficinas.reduce(
    (acc: number, item) => acc + Number(item["40"]),
    0
  );
  const sum60Oficinas = oficinas.reduce(
    (acc: number, item) => acc + Number(item["60"]),
    0
  );
  const sum100Oficinas = oficinas.reduce(
    (acc: number, item) => acc + Number(item.valor_avulso),
    0
  );

  const sumMateriais =
    sumMateriaisContratos +
    sumMateriaisAvulsos +
    sumMateriaisIntensivos +
    sumMateriaisOficinas;
  const sum40 = sum40Contratos + sum40Avulsos + sum40Intensivos + sum40Oficinas;
  const sum60 = sum60Contratos + sum60Avulsos + sum60Intensivos + sum60Oficinas;
  const sum100 =
    sum100Contratos + sum100Avulsos + sum100Intensivos + sum100Oficinas;
  const repasse = sum60 + sumMateriais;

  async function loadProfessores() {
    const weeksData = calculateMonthWeeks();
    const { data, isError } = await professoresApi.listAll({
      query: { relations: ["cursos"] },
    });
    if (isError) {
      Alert.error(data.message || "Erro ao carregar professores");
    }
    const id = data[0]?.id;
    setProfessores(data);
    setFilter((prev) => ({
      ...prev,
      professor_id: id,
      week: weeksData.index,
    }));
    loadResumoContratos(
      id,
      Number(weeksData.index.replace("semana-", "")),
      weeksData.weeks
    );
  }

  async function setContratoPaymentStatus(item: any) {
    const confirmed = await Alert.confirm(
      "Deseja marcar este mês como pago para este aluno?",
      "Marcar como pago?"
    );

    if (!confirmed) return;

    try {
      Alert.await("Definindo pagamento...");
      await alunoApi.definirPagamento({
        aluno_curso_id: item.id,
        ano: filter.year,
        mes: filter.month,
      });

      await Alert.success("Pagamento definido com sucesso!");
    } catch (error) {
      Alert.error(
        (error as any)?.response?.data?.message ||
          "Erro ao definir pagamento para este aluno"
      );
    } finally {
      loadResumoContratos();
    }
  }

  async function loadResumoContratos(
    pid?: number,
    weekIndex?: number,
    weeksList?: any
  ) {
    if (!pid && !filter.professor_id) return;
    console.log(weeks, filter);
    setLoading(true);
    const payload = {
      professor_id: pid ?? filter.professor_id!,
      year: filter.year,
      month: filter.month,
      first_day: (weeksList ?? weeks)[weekIndex ?? filterSemanaIndex].start,
      last_day: (weeksList ?? weeks)[weekIndex ?? filterSemanaIndex].end,
    };
    setCurrentFilter(payload as any);
    const { isError, data } = await professoresApi.getResumoContratos(payload);
    setLoading(false);
    if (isError) {
      Alert.error(data.message || "Erro ao carregar resumo de contratos");
    }
    setResponse(data);
  }

  function calculateMonthWeeks() {
    const data: { start: string; end: string }[] = [];

    const firstDayOfCurrentMonth = dayjs(`${filter.year}-${filter.month}-01`);
    const lastDayOfCurrentMonth = firstDayOfCurrentMonth.endOf("month");

    const firstDayOfCurrentWeek = firstDayOfCurrentMonth.startOf("week");
    const lastDayOfCurrentWeek = lastDayOfCurrentMonth.endOf("week");
    const weeks = lastDayOfCurrentWeek.diff(firstDayOfCurrentWeek, "week") + 1;

    for (let i = 0; i < weeks; i++) {
      const start = firstDayOfCurrentWeek.add(i, "week");
      const end = start.add(6, "day");
      data.push({
        start: start.format("YYYY-MM-DD"),
        end: end.format("YYYY-MM-DD"),
      });
    }

    setWeeks(data);

    const today = new Date();

    // Define a semana atual no filtro
    const currentWeekIndex = data.findIndex(
      (week) => today >= new Date(week.start) && today <= new Date(week.end)
    );

    setFilter((prev) => ({ ...prev, week: `semana-${currentWeekIndex}` }));
    return { index: `semana-${currentWeekIndex}`, weeks: data };
  }

  useEffect(() => {
    loadProfessores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateMonthWeeks();
  }, [filter.year, filter.month]);

  return (
    <PageContainer menuTitle="Pagamentos do Professor">
      <div className={"flex flex-col"}>
        <div className={"flex flex-row justify-between p-2 w-full"}>
          <h1 className={"text-3xl flex items-center"}>
            Pagamentos do Professor
          </h1>
        </div>
      </div>

      <div className="my-10">
        <header className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Select
            label="Professor"
            value={filter.professor_id}
            setValue={(value: any) =>
              setFilter(
                (prev) => ({ ...prev, professor_id: Number(value) } as any)
              )
            }
            options={professores.map((item) => ({
              value: item.id,
              label: item.nome,
            }))}
          />

          <Input
            label="Ano"
            type="number"
            min={2024}
            max={dayjs().year() + 1}
            value={filter.year}
            setValue={(value) =>
              setFilter((prev) => ({ ...prev, year: Number(value) }))
            }
          />
          <Select
            label="Mês"
            value={filter.month}
            setValue={(value: any) =>
              setFilter((prev) => ({ ...prev, month: Number(value) }))
            }
            options={[
              { value: 1, label: "Janeiro" },
              { value: 2, label: "Fevereiro" },
              { value: 3, label: "Março" },
              { value: 4, label: "Abril" },
              { value: 5, label: "Maio" },
              { value: 6, label: "Junho" },
              { value: 7, label: "Julho" },
              { value: 8, label: "Agosto" },
              { value: 9, label: "Setembro" },
              { value: 10, label: "Outubro" },
              { value: 11, label: "Novembro" },
              { value: 12, label: "Dezembro" },
            ]}
          />

          <Select
            label="Semana"
            value={filter.week}
            setValue={(value: any) => {
              setFilter((prev) => ({ ...prev, week: value } as any));
            }}
            options={weeks.map((item, index) => ({
              value: `semana-${index}`,
              label: `Semana ${index + 1} | ${dayjs(item.start).format(
                "DD/MM"
              )} - ${dayjs(item.end).format("DD/MM")}`,
            }))}
          />
        </header>

        <footer className="flex justify-end col-span-12">
          <div className="flex gap-2 mt-4 mr-auto">
            {professor?.cursos?.length === 0 ? (
              <span className="text-red-500">
                Este professor não possui cursos vinculados.
              </span>
            ) : (
              professor?.cursos?.map((curso) => (
                <div className="border rounded h-min px-2">
                  <span className="text-gray-500">{curso.descricao}</span>
                </div>
              ))
            )}
          </div>

          <Button
            className="bg-constancia text-white hover:bg-constancia/80 mt-4"
            type="button"
            onClick={() => loadResumoContratos()}
            loading={loading}
          >
            Pesquisar
          </Button>
        </footer>
      </div>

      <div>
        <div className="grid grid-cols-5 gap-4 col-span-full lg:col-span-9 align-bottom items-center mb-5 mt-5">
          <DashCard label="Total (Sem Materiais)" value={sum100} type="money" />
          <DashCard label="Espaço (40%)" value={sum40} type="money" />
          <DashCard label="Professor (60%)" value={sum60} type="money" />
          <DashCard label="Materiais" value={sumMateriais} type="money" />
          <DashCard
            label="Repasse (60% + Materiais)"
            value={repasse}
            type="money"
          />
        </div>
      </div>

      <BoxedTable
        className="mt-8"
        columns={[
          {
            title: "DIA",
            key: "dia_semana",
            columnGroupClassName: "max-w-[50px]",
          },
          {
            title: "SALA",
            key: "codigo_sala",
            columnGroupClassName: "max-w-[50px]",
          },
          {
            title: "HORÁRIO",
            key: "horario_formatted",
            columnGroupClassName: "max-w-[120px]",
          },
          { title: "ALUNO", sharedClassName: "text-start", key: "aluno_nome" },
          {
            title: "PLA",
            key: "planos_formatted",
            columnGroupClassName: "max-w-[50px]",
          },
          {
            title: "INÍCIO",
            sharedClassName: "text-confianca",
            columnGroupClassName: "max-w-[110px]",
            key: "inicio_formatted",
          },
          {
            title: "FINAL",
            sharedClassName: "text-red-500",
            columnGroupClassName: "max-w-[110px]",
            key: "fim_formatted",
          },
          {
            title: "PGTS",
            key: "pagamento_formatted",
            columnGroupClassName: "max-w-[50px]",
          },
          {
            title: "%",
            key: "porcentagem_desconto_formatted",
            columnGroupClassName: "max-w-[50px]",
          },
          {
            title: "PG",
            key: "pago_formatted",
            columnGroupClassName: "max-w-[50px]",
          },
          {
            title: "100%",
            key: "100_formatted",
            columnGroupClassName: "max-w-[120px]",
            footer: () => Str.currencyMask(sum100Contratos, true),
          },
          {
            title: "Materiais",
            key: "soma_materiais_formatted",
            columnGroupClassName: "max-w-[120px]",
            sharedClassName: "text-green-500",
            footer: () => Str.currencyMask(sumMateriaisContratos, true),
          },
          {
            title: "60%",
            sharedClassName: "text-red-500",
            columnGroupClassName: "max-w-[120px]",
            key: "60_formatted",
            footer: () => Str.currencyMask(sum60Contratos, true),
          },
          {
            title: "40%",
            sharedClassName: "text-confianca",
            columnGroupClassName: "max-w-[120px]",
            key: "40_formatted",
            footer: () => Str.currencyMask(sum40Contratos, true),
          },
          {
            title: "...",
            columnGroupClassName: "max-w-[50px]",
            render: (item) =>
              !!item.pago_formatted ? (
                <></>
              ) : (
                <button
                  className="w-full pb-1 rounded flex items-center justify-center hover:bg-slate-100"
                  type="button"
                  onClick={() => setContratoPaymentStatus(item)}
                >
                  <BiMoneyWithdraw size={18} className="m-0" />
                </button>
              ),
          },
        ]}
        items={contratos}
      />

      {avulsos.length > 0 && (
        <>
          <BoxedTable
            className="mt-8"
            columns={[
              {
                title: "DIA",
                key: "dia_semana",
                columnGroupClassName: "max-w-[50px]",
              },
              {
                title: "MODALIDADE",
                render: () => "AVULSO",
                columnGroupClassName: "max-w-[120px]",
              },
              {
                title: "HORÁRIO",
                key: "horario_formatted",
                columnGroupClassName: "max-w-[120px]",
              },
              {
                title: "ALUNO",
                key: "nome",
                sharedClassName: "w-full",
              },
              {
                title: "DATA",
                key: "data_formatted",
                columnGroupClassName: "max-w-[110px]",
              },
              {
                title: "PGTO",
                key: "pagamento_forma",
                columnGroupClassName: "max-w-[100px]",
              },
              {
                title: "PG",
                key: "pago_formatted",
                columnGroupClassName: "max-w-[50px]",
              },
              {
                title: "100%",
                key: "100_formatted",
                columnGroupClassName: "max-w-[120px]",
                footer: () => Str.currencyMask(sum100Avulsos, true),
              },
              {
                title: "Materiais",
                key: "soma_materiais_formatted",
                columnGroupClassName: "max-w-[120px]",
                sharedClassName: "text-green-500",
                footer: () => Str.currencyMask(sumMateriaisAvulsos, true),
              },
              {
                title: "60%",
                sharedClassName: "text-red-500",
                columnGroupClassName: "max-w-[120px]",
                key: "60_formatted",
                footer: () => Str.currencyMask(sum60Avulsos, true),
              },
              {
                title: "40%",
                sharedClassName: "text-confianca",
                columnGroupClassName: "max-w-[120px]",
                key: "40_formatted",
                footer: () => Str.currencyMask(sum40Avulsos, true),
              },
              {
                title: "",
                columnGroupClassName: "max-w-[50px]",
                disabled: true,
                render: (item) => <></>,
              },
            ]}
            items={avulsos}
          />
        </>
      )}

      {intensivos.length > 0 && (
        <>
          <BoxedTable
            className="mt-8"
            columns={[
              {
                title: "DIA",
                key: "dia_semana",
                columnGroupClassName: "max-w-[50px]",
              },
              {
                title: "MODALIDADE",
                render: () => "INTENSIVO",
                columnGroupClassName: "max-w-[120px]",
              },
              {
                title: "HORÁRIO",
                key: "horario_formatted",
                columnGroupClassName: "max-w-[120px]",
              },
              {
                title: "ALUNO",
                key: "nome",
                sharedClassName: "w-full",
              },
              {
                title: "DATA",
                key: "data_formatted",
                columnGroupClassName: "max-w-[110px]",
              },
              {
                title: "PGTO",
                key: "pagamento_forma",
                columnGroupClassName: "max-w-[100px]",
              },
              {
                title: "PG",
                key: "pago_formatted",
                columnGroupClassName: "max-w-[50px]",
              },
              {
                title: "100%",
                key: "100_formatted",
                columnGroupClassName: "max-w-[120px]",
                footer: () => Str.currencyMask(sum100Intensivos, true),
              },
              {
                title: "Materiais",
                key: "soma_materiais_formatted",
                columnGroupClassName: "max-w-[120px]",
                sharedClassName: "text-green-500",
                footer: () => Str.currencyMask(sumMateriaisIntensivos, true),
              },
              {
                title: "60%",
                sharedClassName: "text-red-500",
                columnGroupClassName: "max-w-[120px]",
                key: "60_formatted",
                footer: () => Str.currencyMask(sum60Intensivos, true),
              },
              {
                title: "40%",
                sharedClassName: "text-confianca",
                columnGroupClassName: "max-w-[120px]",
                key: "40_formatted",
                footer: () => Str.currencyMask(sum40Intensivos, true),
              },
              {
                title: "",
                columnGroupClassName: "max-w-[50px]",
                disabled: true,
                render: (item) => <></>,
              },
            ]}
            items={intensivos}
          />
        </>
      )}

      {oficinas.length > 0 && (
        <>
          <BoxedTable
            className="mt-8"
            columns={[
              {
                title: "DIA",
                key: "dia_semana",
                columnGroupClassName: "max-w-[50px]",
              },
              {
                title: "MODALIDADE",
                render: () => "OFICINA",
                columnGroupClassName: "max-w-[120px]",
              },
              {
                title: "HORÁRIO",
                key: "horario_formatted",
                columnGroupClassName: "max-w-[120px]",
              },
              {
                title: "ALUNO",
                key: "nome",
                sharedClassName: "w-full",
              },
              {
                title: "DATA",
                key: "data_formatted",
                columnGroupClassName: "max-w-[110px]",
              },
              {
                title: "PGTO",
                key: "pagamento_forma",
                columnGroupClassName: "max-w-[100px]",
              },
              {
                title: "PG",
                key: "pago_formatted",
                columnGroupClassName: "max-w-[50px]",
              },
              {
                title: "100%",
                key: "100_formatted",
                columnGroupClassName: "max-w-[120px]",
                footer: () => Str.currencyMask(sum100Oficinas, true),
              },
              {
                title: "Materiais",
                key: "soma_materiais_formatted",
                columnGroupClassName: "max-w-[120px]",
                sharedClassName: "text-green-500",
                footer: () => Str.currencyMask(sumMateriaisOficinas, true),
              },
              {
                title: "60%",
                sharedClassName: "text-red-500",
                columnGroupClassName: "max-w-[120px]",
                key: "60_formatted",
                footer: () => Str.currencyMask(sum60Oficinas, true),
              },
              {
                title: "40%",
                sharedClassName: "text-confianca",
                columnGroupClassName: "max-w-[120px]",
                key: "40_formatted",
                footer: () => Str.currencyMask(sum40Oficinas, true),
              },
              {
                title: "",
                columnGroupClassName: "max-w-[50px]",
                disabled: true,
                render: (item) => <></>,
              },
            ]}
            items={oficinas}
          />
        </>
      )}

      <br />
      <br />
      <br />
      <br />
      <br />
    </PageContainer>
  );
}
